import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface Props {
  cardId: string;
  title: string;
  populatedCount: number;
  totalCount: number;
  defaultOpen?: boolean;
  children: ReactNode;
}

const STORAGE_PREFIX = 'editor.ribbon.';

export function ClinicalDataCard({
  cardId,
  title,
  populatedCount,
  totalCount,
  defaultOpen = true,
  children,
}: Props) {
  const storageKey = `${STORAGE_PREFIX}${cardId}`;
  const bodyId = useId();
  const [open, setOpen] = useState<boolean>(() => {
    const stored = globalThis.localStorage.getItem(storageKey);
    if (stored === null) return defaultOpen;
    return stored === 'true';
  });

  useEffect(() => {
    globalThis.localStorage.setItem(storageKey, open ? 'true' : 'false');
  }, [storageKey, open]);

  const isComplete = populatedCount === totalCount && populatedCount > 0;
  const isEmpty = populatedCount === 0;
  let countLabel = '';
  if (isEmpty) countLabel = 'Empty';
  else if (isComplete) countLabel = 'Done';
  else countLabel = `${String(populatedCount)} / ${String(totalCount)}`;

  return (
    <section
      className={cn(
        'border-accent-ink/8 group rounded-2xl border bg-white transition-shadow',
        open && 'shadow-xs',
      )}
    >
      <button
        type="button"
        onClick={() => {
          setOpen((p) => !p);
        }}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full items-center justify-between gap-2 px-5 pt-4 pb-3 text-left transition-colors hover:bg-neutral-50/40"
      >
        <span className="font-display tracking-eyebrow text-accent-ink text-[11px] font-medium uppercase italic">
          {title}
        </span>
        <span className="flex items-center gap-2">
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 font-mono text-[10px] tracking-[0.04em] uppercase',
              isEmpty && 'text-neutral-400',
              !isEmpty && !isComplete && 'bg-primary-50 text-primary-700',
              isComplete && 'bg-primary-100/70 text-primary-700',
            )}
          >
            {!isEmpty ? (
              <span
                aria-hidden
                className={cn(
                  'h-1 w-1 rounded-full',
                  isComplete ? 'bg-primary-500' : 'bg-primary-400',
                )}
              />
            ) : null}
            {countLabel}
          </span>
          <ChevronDown
            className={cn(
              'text-accent-ink/40 h-4 w-4 transition-transform motion-reduce:transition-none',
              open && 'rotate-180',
            )}
            strokeWidth={1.5}
          />
        </span>
      </button>
      {open ? (
        <div id={bodyId} className="border-accent-ink/8 border-t px-5 pt-4 pb-4">
          {children}
        </div>
      ) : null}
    </section>
  );
}
